/**
 * NATLOC Validators
 * 
 * ESTADO: FUNCIONAL ✅
 * Estos validadores pueden usarse inmediatamente
 */

import { CUPS_VALIDATION, VERTICAL_ADDRESS_FORMATS } from './constants'

/**
 * Validate CUPS format
 * @param {string} cups - CUPS code to validate
 * @param {string} category - Supply category (electricity/gas)
 * @returns {Object} Validation result { isValid: boolean, error: string|null }
 */
export const validateCups = (cups, category = 'electricity') => {
  if (!cups || typeof cups !== 'string') {
    return { isValid: false, error: 'CUPS is required' }
  }

  const trimmedCups = cups.trim().toUpperCase()
  
  // Check length
  const minLength = CUPS_VALIDATION.MIN_LENGTH
  const maxLength = category === 'gas' ? CUPS_VALIDATION.GAS_LENGTH : CUPS_VALIDATION.MAX_LENGTH
  
  if (trimmedCups.length < minLength || trimmedCups.length > maxLength) {
    return { isValid: false, error: 'Invalid CUPS length' }
  }

  // Check format
  if (!trimmedCups.startsWith('ES')) {
    return { isValid: false, error: 'CUPS must start with ES' }
  }

  // Validate with regex
  const match = trimmedCups.match(CUPS_VALIDATION.REGEX)
  if (!match) {
    return { isValid: false, error: 'Invalid CUPS format' }
  }

  // Validate control digits
  const digits = match[1]
  const letters = match[2]
  
  const numValue = parseInt(digits, 10)
  const remainder = numValue % 529
  const expectedLetters = 
    CUPS_VALIDATION.CONTROL_LETTERS[Math.floor(remainder / 23)] + 
    CUPS_VALIDATION.CONTROL_LETTERS[remainder % 23]

  if (letters !== expectedLetters) {
    return { isValid: false, error: 'Invalid CUPS control digits' }
  }

  return { isValid: true, error: null }
}

/**
 * Validate building/block format
 * @param {string} building - Building identifier
 * @returns {Object} Validation result
 */
export const validateBuilding = (building) => {
  if (!building || building.trim() === '') {
    return { isValid: true, error: null } // Optional field
  }

  const isValid = VERTICAL_ADDRESS_FORMATS.BUILDING.test(building.trim().toUpperCase())
  return {
    isValid,
    error: isValid ? null : 'Invalid building format. Use 1-99, A-Z, or BIS',
  }
}

/**
 * Validate staircase format
 * @param {string} staircase - Staircase identifier
 * @returns {Object} Validation result
 */
export const validateStaircase = (staircase) => {
  if (!staircase || staircase.trim() === '') {
    return { isValid: true, error: null } // Optional field
  }

  const isValid = VERTICAL_ADDRESS_FORMATS.STAIRCASE.test(staircase.trim().toUpperCase())
  return {
    isValid,
    error: isValid ? null : 'Invalid staircase format. Use 1-99, A-Z, INT, EXT, IZQ, or DER',
  }
}

/**
 * Validate floor format
 * @param {string} floor - Floor identifier
 * @returns {Object} Validation result
 */
export const validateFloor = (floor) => {
  if (!floor || floor.trim() === '') {
    return { isValid: true, error: null } // Optional field
  }

  const isValid = VERTICAL_ADDRESS_FORMATS.FLOOR.test(floor.trim().toUpperCase())
  return {
    isValid,
    error: isValid ? null : 'Invalid floor format',
  }
}

/**
 * Validate door format
 * @param {string} door - Door identifier
 * @returns {Object} Validation result
 */
export const validateDoor = (door) => {
  if (!door || door.trim() === '') {
    return { isValid: true, error: null } // Optional field
  }

  const isValid = VERTICAL_ADDRESS_FORMATS.DOOR.test(door.trim().toUpperCase())
  return {
    isValid,
    error: isValid ? null : 'Invalid door format. Use 1-99, A-Z, IZQ, or DER',
  }
}

/**
 * Validate complete vertical address
 * @param {Object} address - Address object
 * @param {string} address.building - Building
 * @param {string} address.staircase - Staircase
 * @param {string} address.floor - Floor
 * @param {string} address.door - Door
 * @returns {Object} Validation result with errors for each field
 */
export const validateVerticalAddress = (address) => {
  const buildingValidation = validateBuilding(address.building)
  const staircaseValidation = validateStaircase(address.staircase)
  const floorValidation = validateFloor(address.floor)
  const doorValidation = validateDoor(address.door)

  const isValid = 
    buildingValidation.isValid &&
    staircaseValidation.isValid &&
    floorValidation.isValid &&
    doorValidation.isValid

  return {
    isValid,
    errors: {
      building: buildingValidation.error,
      staircase: staircaseValidation.error,
      floor: floorValidation.error,
      door: doorValidation.error,
    },
  }
}

/**
 * Validate postal code format (Spanish)
 * @param {string} postalCode - Postal code
 * @returns {Object} Validation result
 */
export const validatePostalCode = (postalCode) => {
  if (!postalCode || postalCode.trim() === '') {
    return { isValid: false, error: 'Postal code is required' }
  }

  const trimmed = postalCode.trim()
  
  // Spanish postal codes are 5 digits
  if (!/^\d{5}$/.test(trimmed)) {
    return { isValid: false, error: 'Postal code must be 5 digits' }
  }

  // First two digits represent province (01-52)
  const province = parseInt(trimmed.substring(0, 2), 10)
  if (province < 1 || province > 52) {
    return { isValid: false, error: 'Invalid province code' }
  }

  return { isValid: true, error: null }
}
